import type { SiteBuilderBlock } from "./siteBuilderTypes";

export type SiteBuilderFieldScope = "content" | "options";

export type SiteBuilderFieldDefaultDefinition = {
    contextualDefaults?: {
        content?: Record<string, unknown>;
        options?: Record<string, unknown>;
    };
    responsive?: {
        stackOnMobile?: boolean;
    };
};

type FieldAliasMap = Readonly<
    Record<string, Readonly<Record<string, readonly string[]>>>
>;

const CONTENT_FIELD_ALIASES: FieldAliasMap = {
    alert: {
        text: ["title", "label"],
    },
    avatars: {
        items: ["names", "text"],
    },
    badge: {
        href: ["url"],
        label: ["text", "title"],
    },
    blockquote: {
        text: ["quote"],
    },
    button: {
        href: ["url"],
        label: ["text"],
    },
    code: {
        code: ["text"],
    },
    countdown: {
        targetDate: ["date", "label", "text"],
    },
    ctaBadge: {
        href: ["url"],
        label: ["text"],
    },
    donationButton: {
        href: ["url"],
        label: ["text"],
    },
    iconList: {
        items: ["text"],
    },
    image: {
        image: ["imageUrl", "src", "url"],
    },
    link: {
        href: ["url"],
        label: ["text"],
    },
    map: {
        address: ["text"],
        mapUrl: ["url", "embedUrl"],
    },
    media: {
        image: ["imageUrl", "src", "url"],
        videoUrl: ["embedUrl"],
    },
    progressBar: {
        progressValue: ["value"],
    },
    quote: {
        text: ["quote"],
    },
    rating: {
        ratingValue: ["value", "rating"],
    },
    socialLink: {
        href: ["url"],
        label: ["text"],
    },
    supportedPaymentButton: {
        items: ["text"],
    },
    video: {
        videoUrl: ["url", "embedUrl"],
    },
};

const OPTION_FIELD_ALIASES: FieldAliasMap = {
    "*": {
        alignment: ["horizontalAlign"],
        backdropBlur: ["backdropFilter"],
    },
    icon: {
        size: ["fontSize", "iconSize"],
    },
    image: {
        objectFit: ["imageObjectFit"],
    },
    media: {
        objectFit: ["imageObjectFit"],
    },
    rating: {
        ratingSize: ["ratingIconSize"],
    },
    socialMedia: {
        socialIconColor: ["iconColor"],
    },
};

const OPTION_FIELD_CONFLICTS: FieldAliasMap = {
    addToCartButton: {
        size: [
            "fontSize",
            "minHeight",
            "padding",
            "paddingBottom",
            "paddingLeft",
            "paddingRight",
            "paddingTop",
        ],
    },
    blockquote: {
        blockquoteShape: ["borderRadius"],
        edgeSpacing: ["padding"],
    },
    button: {
        buttonShape: ["borderRadius"],
        size: [
            "fontSize",
            "minHeight",
            "padding",
            "paddingBottom",
            "paddingLeft",
            "paddingRight",
            "paddingTop",
        ],
    },
    ctaBadge: {
        buttonShape: ["borderRadius"],
        size: [
            "fontSize",
            "minHeight",
            "padding",
            "paddingBottom",
            "paddingLeft",
            "paddingRight",
            "paddingTop",
        ],
    },
    donationButton: {
        buttonShape: ["borderRadius"],
        size: [
            "fontSize",
            "minHeight",
            "padding",
            "paddingBottom",
            "paddingLeft",
            "paddingRight",
            "paddingTop",
        ],
    },
    image: {
        shape: ["borderRadius"],
    },
    media: {
        shape: ["borderRadius"],
    },
};

function fieldRecord(
    block: SiteBuilderBlock,
    scope: SiteBuilderFieldScope,
): Record<string, unknown> {
    return scope === "content"
        ? block.content ?? {}
        : block.options ?? {};
}

export function siteBuilderFieldAliasKeys(
    blockType: string,
    scope: SiteBuilderFieldScope,
    key: string,
): readonly string[] {
    const aliases = scope === "content"
        ? CONTENT_FIELD_ALIASES
        : OPTION_FIELD_ALIASES;

    return [
        ...(aliases["*"]?.[key] ?? []),
        ...(aliases[blockType]?.[key] ?? []),
    ];
}

function normalizedAliasValue(
    scope: SiteBuilderFieldScope,
    key: string,
    alias: string,
    value: unknown,
): unknown {
    if (
        scope === "options"
        && key === "backdropBlur"
        && alias === "backdropFilter"
        && typeof value === "string"
    ) {
        return value.match(/blur\(([^)]+)\)/iu)?.[1] ?? value;
    }

    return value;
}

/**
 * Resolves the current schema field first and only falls back to legacy
 * snapshot names when the canonical key is genuinely absent.
 *
 * Empty canonical values deliberately win. This lets a user clear a value
 * without an older alias unexpectedly becoming visible again.
 */
export function siteBuilderFieldValue(
    block: SiteBuilderBlock,
    scope: SiteBuilderFieldScope,
    key: string,
): unknown {
    const values = fieldRecord(block, scope);

    if (Object.prototype.hasOwnProperty.call(values, key)) {
        return values[key];
    }

    for (const alias of siteBuilderFieldAliasKeys(block.type, scope, key)) {
        if (Object.prototype.hasOwnProperty.call(values, alias)) {
            return normalizedAliasValue(
                scope,
                key,
                alias,
                values[alias],
            );
        }
    }

    return undefined;
}

export function siteBuilderFieldDefault(
    block: SiteBuilderBlock,
    definition: SiteBuilderFieldDefaultDefinition,
    scope: SiteBuilderFieldScope,
    key: string,
): unknown {
    if (scope === "options" && key === "stackOnMobile") {
        return definition.responsive?.stackOnMobile;
    }

    if (scope === "options" && key === "openInNewWindow") {
        const href = String(
            siteBuilderFieldValue(block, "content", "href") ?? "",
        );

        return /^https?:\/\//iu.test(href);
    }

    return scope === "content"
        ? definition.contextualDefaults?.content?.[key]
        : definition.contextualDefaults?.options?.[key];
}

/**
 * Writes one canonical inspector value and removes every competing legacy
 * alias. Renderers can then prefer the current schema key without a stale
 * snapshot value masking the user's edit.
 */
export function withSiteBuilderFieldValue(
    block: SiteBuilderBlock,
    scope: SiteBuilderFieldScope,
    key: string,
    value: unknown,
): SiteBuilderBlock {
    const values = {
        ...fieldRecord(block, scope),
        [key]: value,
    };

    for (const alias of siteBuilderFieldAliasKeys(block.type, scope, key)) {
        delete values[alias];
    }
    if (scope === "options" && value !== "" && value !== undefined) {
        for (const conflict of OPTION_FIELD_CONFLICTS[block.type]?.[key] ?? []) {
            delete values[conflict];
        }
    }

    return scope === "content"
        ? {
            ...block,
            content: values,
        }
        : {
            ...block,
            options: values,
        };
}
